import { UserProfile } from '@brandclub/types';
import { ExtendedGetResult } from '@fingerprintjs/fingerprintjs-pro';
import _get from 'lodash/get';
import mixpanelLib, { Response } from 'mixpanel-browser';
import queryString from 'qs';
import { VisitorTracking } from '../types/AnalyticsTracking';
import BrandclubStoreConfigUtils from '../utils/brandclubStoreConfigUtils';
import { getRoutePath } from '../utils/domain';
import { AnalyticsTrackingEvent } from './events';
import * as ExternalTracking from './externalTracking';

type trackingEventType = AnalyticsTrackingEvent | `tombstone: ${string}`;

export const deferredPromise = <T = unknown>() => {
  let resolve: (value: T) => void = () => void 0;
  let reject: (reason?: Error | unknown) => void = () => void 0;
  let isDone = false;
  const p = new Promise<T>((r, e) => {
    resolve = (v) => {
      isDone = true;
      r(v);
    };
    reject = (err) => {
      isDone = true;
      e(err);
    };
  });
  return { p, resolve, reject, isDone: () => isDone } as DeferredPromise<T>;
};

export type DeferredPromise<T> = {
  p: Promise<T>;
  isDone: () => boolean;
  resolve: (value: T) => void;
  reject: (reason?: Error | unknown) => void;
};

let isInitialized = false;

/**
 * The Mixpanel SDK this module drives — THIS bundle's own copy by default.
 *
 * It is a `let` (not the bare import) so a host that already runs an initialized
 * SDK can hand its instance over via `adoptMixpanelInstance` and have this module
 * publish through THAT one. The case: the Pages storefront loads the ShopperOS
 * Checkout App as a separate `library: var` webpack bundle, which carries its own
 * copies of `mixpanel-browser` AND of this module — so without adoption the page
 * runs two SDK instances, each with its own `api_host`, batcher, autocapture
 * (a duplicate `$mp_web_page_view` per load), device id, and — decisively — only
 * the storefront's instance has the events-ingest → Doris mirror registered on it,
 * so anything the other bundle emits (concierge) never reaches the warehouse.
 * Adoption collapses both to the host's single mirrored instance.
 */
let mixpanel: typeof mixpanelLib = mixpanelLib;

/** The SDK instance this module publishes through — for a host to hand to an
 *  embedded bundle (see `adoptMixpanelInstance`). */
export function getMixpanelInstance(): typeof mixpanelLib {
  return mixpanel;
}

/**
 * Publish through the HOST's already-initialized SDK instead of this bundle's own.
 * Marks this module initialized, so `initializeMixpanel` is skipped by every
 * `isMixpanelInitialized()` guard — the host owns the config (`api_host`, project
 * token, autocapture). Call BEFORE the first `track`; adopting later would strand
 * the events already sent through this bundle's own instance.
 */
export function adoptMixpanelInstance(instance: typeof mixpanelLib): void {
  mixpanel = instance;
  isInitialized = true;
}

const __PROD__ = BrandclubStoreConfigUtils.getAppStage() === 'prod';

const DEBUG = !__PROD__ && false;

if (DEBUG) {
  ExternalTracking.enableDebug();
}
type VisitorTrackingSnapshot =
  | {
      uniqueVisitorId: string | null;
      referrer: string | null;
      fingerprintResult?: ExtendedGetResult;
    }
  | undefined;

const currentVisitorTracking: DeferredPromise<VisitorTrackingSnapshot> = deferredPromise();

/**
 * How long `track` waits for visitor identity before it sends the event without it.
 *
 * `currentVisitorTracking` resolves ONLY when a host calls
 * `setCurrentVisitorTracking` with a visitor id, and a host does that after auth
 * and the profile settle. On a surface where they never settle, an unbounded
 * `await` made every `track` call hang forever: no event, no error, no console
 * warning and no network request. The concierge embedded in a merchant Shopify
 * storefront hit exactly that — it minted an attribution touch and stamped the
 * cart, but no qualifying event ever reached Mixpanel, so purchase attribution
 * had nothing to corroborate the cart against.
 *
 * The identity only ENRICHES the event with ip and location. It is not required
 * to send one, so the wait is bounded and a miss costs enrichment, never the event.
 */
const VISITOR_TRACKING_MAX_WAIT_MS = 2000;

/**
 * The visitor identity if it arrives in time, otherwise undefined.
 *
 * `track` then takes the branch that sends without the ip/location enrichment.
 */
async function visitorTrackingWithinDeadline(): Promise<VisitorTrackingSnapshot> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      currentVisitorTracking.p,
      new Promise<undefined>((resolve) => {
        timer = setTimeout(() => resolve(undefined), VISITOR_TRACKING_MAX_WAIT_MS);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export function setCurrentVisitorTracking(visitorTracking?: VisitorTracking) {
  if (visitorTracking && visitorTracking.uniqueVisitorId) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[DEBUG] resolving ', visitorTracking);
    }
    currentVisitorTracking.resolve(visitorTracking);
  }
}

export async function track(
  eventName: AnalyticsTrackingEvent | `tombstone: ${string}`,
  eventProps: { [key: string]: any },
  options?: {
    sendImmediately?: boolean;
  },
): Promise<
  | {
      eventName: string;
      eventParams: Record<string, any>;
    }
  | undefined
> {
  // Sends event to mixpanel
  const { location } = window;
  const params = queryString.parse(location.search, {
    ignoreQueryPrefix: true,
    arrayLimit: 100,
  });
  const pathName = _get(location.pathname.split('/'), '1') as unknown as string;
  const eventNameForService = `${eventName}`;
  const { brandPixels, ...rest } = eventProps || {};
  // URL params are fill-ins only — the event's own properties must win. A page
  // URL carrying e.g. ?brandId= must never overwrite the brandId the emitter
  // set, or a cross-brand id in the URL poisons the event (and everything
  // downstream that aggregates it, e.g. concierge purchase attribution).
  const paramsForService: Record<string, any> = {
    ...params,
    ...rest,
    pathName: pathName?.toLowerCase(),
    referrer: document.referrer,
    retailerId: rest?.retailerId || params.rid,
  };

  try {
    await ExternalTracking.track(eventNameForService, paramsForService, brandPixels);
  } catch (e) {
    console.error(`Error sending event (${eventNameForService}) to external tracking: ${(e as any).message}`);
  }

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log(`[DEBUG] sending '${eventNameForService}' with params: `, paramsForService);
  }

  try {
    if (!isInitialized) {
      initializeMixpanel();
    }
    if (isInitialized) {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.log(`[DEBUG] Sending ${eventNameForService} to Mixpanel`);
      }
      const visitorTracking = await visitorTrackingWithinDeadline();
      return new Promise((resolve, reject) => {
        try {
          if (visitorTracking && visitorTracking.fingerprintResult) {
            mixpanel.track(
              eventNameForService,
              {
                ...paramsForService,
                ipAddress: visitorTracking.fingerprintResult.ip,
                timeZone: visitorTracking.fingerprintResult.ipLocation?.timezone,
                location: {
                  city: visitorTracking.fingerprintResult.ipLocation?.city, // The city where the endpoint is located.
                  country: visitorTracking.fingerprintResult.ipLocation?.country?.code, // The two-letter code for the country or region of the endpoint. Specified as an ISO 3166-1 alpha-2 code, such as "US" for the United States.
                  latitude: visitorTracking.fingerprintResult.ipLocation?.latitude, // The latitude of the endpoint location, rounded to one decimal place.
                  longitude: visitorTracking.fingerprintResult.ipLocation?.longitude, // The longitude of the endpoint location, rounded to one decimal place.
                  postalCode: visitorTracking.fingerprintResult.ipLocation?.postalCode, // The postal code or zip code of the endpoint.
                },
              },
              {
                send_immediately: options?.sendImmediately,
              },
              (response: Response) => {
                if (response === 0 || (response as any)?.status === 0) {
                  reject(new Error('Mixpanel event not sent'));
                } else {
                  resolve({ eventName: eventNameForService, eventParams: paramsForService });
                }
              },
            );
          } else {
            mixpanel.track(
              eventNameForService,
              paramsForService,
              {
                send_immediately: options?.sendImmediately,
              },
              (response: Response) => {
                if (response === 0 || (response as any)?.status === 0) {
                  reject(new Error('Mixpanel event not sent'));
                } else {
                  resolve({ eventName: eventNameForService, eventParams: paramsForService });
                }
              },
            );
          }
        } catch (e) {
          reject(e);
        }
      });
    } else {
      console.error('mixpanel not initialized');
    }
  } catch (e) {
    console.error(`Error sending event (${eventNameForService}) to Mixpanel: ${(e as any).message}`);
  }
}

/**
 *  Logs to mixpanel if in prod, but warns to the console if not.
 *  @param msg a description of the event to be logged
 *  @param obj an optional object of related data to log with the message
 *  @param eventName an optional name for the event, defaults to 'warning'
 * */
export function warn(
  msg: string,
  obj?: { [key: string]: any },
  eventName: trackingEventType = AnalyticsTrackingEvent.WARNING,
) {
  if (__PROD__) {
    track(eventName, obj || {});
  } else if (obj) {
    console.warn(msg, obj);
  } else {
    console.warn(msg);
  }
}
/**
 *  Logs to mixpanel if in prod, but errors in the console if not.
 *  @param msg a description of the event to be logged
 *  @param obj an optional object of related data to log with the message
 *  @param eventName an optional name for the Mixpanel event, defaults to 'error'
 * */
export function error(
  msg: string,
  obj?: { [key: string]: any },
  eventName: trackingEventType = AnalyticsTrackingEvent.ERROR,
) {
  if (__PROD__) {
    track(eventName, obj ? { ...obj, message: msg } : { message: msg });
  } else if (obj) {
    console.error(msg, obj);
  } else {
    console.error(msg);
  }
}

/**
 * This function should be called when the application encounters an untenable state and cannot continue.  It will throw
 * an `Error` with the provided message and log the provided metadata object to the console if provided.  If in production,
 * it will trigger a Mixpanel event for the error and supply the provided metadata.
 *
 * @param msg a description of the error or unhandleable event that occurred
 * @param obj an optional object of related data to log with the message
 * @param eventName an optional name for the Mixpanel event, defaults to 'return panic'
 */
export function panic(
  msg: string,
  obj?: { [key: string]: any },
  eventName: trackingEventType = AnalyticsTrackingEvent.RETURN_PANIC,
): never {
  if (__PROD__) {
    track(eventName, obj ? { ...obj, msg } : { msg });
  } else if (obj) {
    console.error(msg, obj);
  }

  throw new Error(msg);
}

/**
 * Where the SDK posts events.
 *
 * Normally this is `origin + routePath`, a first-party path so ad blockers do not eat
 * the events. That is correct on two surfaces: the Brandclub front room, whose origin is
 * ours and serves `/track`, and the Shopify App Proxy, where the injected route path
 * reaches our same-origin forwarder.
 *
 * It is wrong on a third. A concierge mounted on a Shopify STOREFRONT by our theme app
 * embed has no App Proxy route, so `origin + ''` is the merchant's own storefront and
 * `/track` answers 404 — every event is posted into a void, silently. Measured on
 * Storkcraft: a cart carried a valid concierge touch whose token appeared on ZERO events
 * in three weeks, so the resolver could never corroborate it and the sale could never be
 * attributed.
 *
 * On that surface we give up the first-party path and let the SDK use Mixpanel's own
 * host. Losing some events to ad blockers beats losing all of them.
 *
 * `bcStoreKind` is published by our theme app embed and by nothing else, so this reads a
 * host DECLARATION rather than guessing the surface from the page.
 *
 * `origin` is a parameter only so tests can name a surface; callers pass nothing.
 */
export const resolveMixpanelApiHost = (origin: string = window.location.origin): string | undefined => {
  // Use process.env.NODE_ENV for compatibility with Jest/Node environments
  // In Vite environments, import.meta.env.MODE would be available but we prioritize process.env.NODE_ENV
  const nodeEnv = process.env.NODE_ENV;
  if (nodeEnv === 'development') {
    return undefined; // will default to normal mixpanel host
  }

  const routePath = getRoutePath();
  const onMerchantStorefront = (window as { bcStoreKind?: string }).bcStoreKind === 'shopify';
  if (onMerchantStorefront && routePath === '') {
    return undefined; // no proxy route here — the storefront origin would 404
  }

  return `${origin}${routePath}`;
};

const MIXPANEL_PROJECT_TOKEN_PROD = '5f5add70c60a83f55ad7ba6d75faf5db';
const MIXPANEL_PROJECT_TOKEN_NONPROD = 'cde1743169537cd47bc24fe7f0ee14b0';

/**
 * Prod-vs-dev TIER of a deploy stage — the browser-side mirror of
 * `@brandclub/shared` `isProdAuthStage` (client bundles can't import the backend
 * package). `gamma` is a PROD-tier ring (shares prod data/secrets/cookies), so
 * it uses the prod Mixpanel project. Keep in sync with the canonical predicate.
 * Total over any string: unknown / `dev` / `beta` / `localhost` → non-prod.
 */
export function isProdTierStage(stage: string): boolean {
  return stage === 'prod' || stage === 'production' || stage === 'gamma';
}

/**
 * @param appStageOverride when set, picks the Mixpanel project by this deploy
 * stage's TIER (`isProdTierStage`, not a bare `=== 'prod'`) instead of the
 * origin/NODE_ENV heuristic (`getAppStage()`). Hosts that already know their
 * stage at build time (e.g. the Pages storefront via SST-injected
 * `NEXT_PUBLIC_STAGE` = `dev`|`beta`|`gamma`|`prod`) pass it so the project
 * follows the DEPLOY tier, not the URL — a `next dev` build served on a prod
 * host no longer silently lands in the non-prod project. `localhost` is NOT a
 * stage: local dev passes `dev`, and NODE_ENV carries the "am I local" signal
 * separately. The arg-less fallback (`__PROD__`) is unchanged for callers that
 * haven't opted in.
 */
export function initializeMixpanel(domainOverride?: string, appStageOverride?: string) {
  const isProd = appStageOverride ? isProdTierStage(appStageOverride) : __PROD__;
  mixpanel.init(isProd ? MIXPANEL_PROJECT_TOKEN_PROD : MIXPANEL_PROJECT_TOKEN_NONPROD, {
    autocapture: {
      pageview: 'full-url',
      input: true,
      submit: true,
      capture_text_content: false,
      click: false,
      scroll: false,
    },
    ignore_dnt: true,
    api_host: domainOverride || resolveMixpanelApiHost(),
    // Use sendBeacon for reliability during page transitions
    transport: 'sendBeacon',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any);
  isInitialized = true;
}

/**
 * Mirror first-party Mixpanel events to the shared events-ingest pipe
 * (`events-ingest.brandclub.com/v1/track` → Kinesis → Doris `mixpanel.raw_events`
 * → pages-insights) by PIGGYBACKING on Mixpanel's own `RequestBatcher`.
 *
 * We register a `before_send_events` hook, which Mixpanel runs per event inside
 * its batch-flush loop — so we inherit Mixpanel's queue, batching cadence,
 * flush-on-unload and retries for free (NO custom batcher), AND the event we
 * mirror is Mixpanel's OWN fully-enriched payload ($os / $browser / $device_id /
 * distinct_id / super-props / geo, plus everything we set).
 *
 * BI suppression: an event tagged `__mpSuppress` (the storefront's warehouse-only
 * "standard" stream) is still MIRRORED to Doris but the hook returns `null` so
 * Mixpanel drops it from the BI project — no count inflation. Legacy events return
 * the payload unchanged (Mixpanel keeps sending them). `__mpSuppress` is stripped
 * from the mirrored copy. The hook MUST return the event (or null) — returning
 * undefined makes Mixpanel report an error and drop it.
 *
 * Scoped to events carrying an app identity (first-party) so Mixpanel autocapture isn't
 * mirrored. Fire-and-forget, never throws. Empty `url` disables it. Idempotent.
 */
/** True once a non-empty ingest URL was hooked onto Mixpanel's batcher. */
let eventsIngestMirrorEnabled = false;
export function isEventsIngestMirrorEnabled(): boolean {
  return eventsIngestMirrorEnabled;
}
export function registerEventsIngestMirror(url: string | undefined | null): void {
  if (typeof window === 'undefined' || !url || eventsIngestMirrorEnabled) return;
  try {
    const mp = mixpanel as unknown as {
      add_hook?: (
        name: string,
        fn: (payload: { event?: string; properties?: Record<string, unknown> }) => unknown,
      ) => void;
    };
    if (typeof mp.add_hook !== 'function') return;
    mp.add_hook('before_send_events', (payload) => {
      try {
        const props = payload?.properties || {};
        // First-party events carry appId (legacy) or app_id (standard stream);
        // skip Mixpanel autocapture (pageview/input/submit) which carries neither.
        if (props['appId'] || props['app_id']) {
          // `__mpSuppress` (the storefront's warehouse-only STANDARD stream) is
          // dropped from the Mixpanel BI project AND is NOT mirrored to Doris: it
          // shares its `$insert_id` with the legacy event, so mirroring it would
          // UNIQUE-KEY-clobber the legacy row that pages-insights reads. So today
          // only the LEGACY event reaches Doris; standard rides Mixpanel's batcher
          // but reaches neither sink (dormant until it gets its own id/table).
          if (props['__mpSuppress']) return null; // drop from BI, skip the Doris mirror
          const timeSec = props['time'];
          const eventTimestamp = new Date(typeof timeSec === 'number' ? timeSec * 1000 : Date.now()).toISOString();
          fetch(url, {
            method: 'POST',
            mode: 'cors',
            credentials: 'omit',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              eventId: props['$insert_id'] ?? `${props['time'] ?? Date.now()}`,
              eventName: payload.event,
              eventTimestamp,
              properties: props,
            }),
          }).catch(() => undefined);
        }
      } catch {
        /* the mirror must never break Mixpanel's own send */
      }
      return payload; // REQUIRED: return the event so Mixpanel still sends it
    });
    eventsIngestMirrorEnabled = true;
  } catch {
    /* add_hook unavailable → skip mirror silently */
  }
}

export function isMixpanelInitialized() {
  return isInitialized;
}

export function setMixpanelUserIdentity(visitorTracking?: VisitorTracking, user?: UserProfile | null) {
  if (user) {
    const { given_name: firstName, family_name: lastName, email, userId } = user;
    mixpanel.identify(userId);
    mixpanel.register({
      FirstName: firstName,
      LastName: lastName,
      Email: email,
      UniqueVisitorId: visitorTracking?.uniqueVisitorId,
    });

    mixpanel.people.set({
      $first_name: firstName,
      $last_name: lastName,
      $email: email,
      gender: (user as any).genderPredicted || user.gender,
    });
  } else if (visitorTracking?.uniqueVisitorId) {
    mixpanel.identify(visitorTracking?.uniqueVisitorId);
  }
}

/**
 * Used to mark potentially dead code for future deletion. When code could potentially be dead, but you aren't sure,
 * put a tombstone in it and check the logs if it was ever hit. This helps make dead code deletion safer.
 * Idea taken from https://www.bugsnag.com/blog/javascript-refactoring-with-bugsnag-and-tombstones
 *
 * @param msg a custom message for mixpanel, should include the file and line (e.g. operations.ts:113)
 * @param obj an optional object to log with the message.
 */
export function tombstone(msg: string, obj: any = {}) {
  track(`tombstone: ${msg}`, obj);
}

export function trackRouteChange(previousLocation: Location, nextLocation: Location) {
  const params = queryString.parse(nextLocation.search, {
    ignoreQueryPrefix: true,
    arrayLimit: 100,
  });

  track(AnalyticsTrackingEvent.PAGE_VIEW, {
    ...params,
    currentUrl: nextLocation.pathname,
    previousUrl: previousLocation.pathname,
  });
}

export const trackError = (url: string, err: Error) => {
  track(AnalyticsTrackingEvent.ERROR_PAGE, {
    url,
    message: err.message,
    backtrace: err.stack || '',
    lineNumber: (err as any).lineNumber,
  });
};
